
import React, { createContext, useContext } from 'react';
import { useSecureAuth } from '@/hooks/useSecureAuth';
import { User, Session } from '@supabase/supabase-js';
import { Loader2 } from 'lucide-react';

interface SecureAuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<{ error: any }>;
  loading: boolean;
  authAttempts: number;
}

const SecureAuthContext = createContext<SecureAuthContextType | undefined>(undefined);

export const useSecureAuthContext = () => {
  const context = useContext(SecureAuthContext);
  if (context === undefined) {
    throw new Error('useSecureAuthContext must be used within a SecureAuthProvider');
  }
  return context;
};

export const SecureAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const authData = useSecureAuth();
  
  // Show loading screen while auth is initializing
  if (authData.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-white" />
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <SecureAuthContext.Provider value={authData}>
      {children}
    </SecureAuthContext.Provider>
  );
};
